import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageHero } from './SitePageTemplates';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface SimpleCardItem {
  icon: IconType;
  title: string;
  description: string;
}

interface ListCardItem {
  icon: IconType;
  title: string;
  points: string[];
}

interface AdvantageItem {
  icon: IconType;
  title: string;
  description: string;
  accent?: string;
}

interface StatItem {
  value: string;
  label: string;
}

interface SolutionPageTemplateProps {
  eyebrow: string;
  title: string;
  highlight?: string;
  description: string;
  heroIcon: IconType;
  heroAccentIcon: IconType;
  problemHeading: string;
  problemDescription: string;
  painPoints: SimpleCardItem[];
  solutionsIntro: string;
  solutions: ListCardItem[];
  whyChooseCards: AdvantageItem[];
  ctaTitle: string;
  ctaDescription: string;
}

interface IndustryPageTemplateProps {
  eyebrow: string;
  title: string;
  highlight?: string;
  description: string;
  heroIcon: IconType;
  heroAccentIcon: IconType;
  challengesIntro: string;
  challenges: SimpleCardItem[];
  solutionsIntro: string;
  solutions: SimpleCardItem[];
  useCase: string;
  stats: StatItem[];
  ctaTitle: string;
  ctaDescription: string;
}

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.75 },
};

const sharedMetrics: StatItem[] = [
  { value: '140+', label: 'Countries supported' },
  { value: '6', label: 'Global offices' },
  { value: '24/7', label: 'Control tower' },
];

const warehouseTheme = process.env.REACT_APP_LANDING === 'warehouse';

const renderHighlightedTitle = (title: string, highlight?: string) => {
  if (!highlight) return title;

  const index = title.indexOf(highlight);
  if (index === -1) return title;

  const before = title.slice(0, index);
  const after = title.slice(index + highlight.length);

  return (
    <>
      {before}
      <span className="bg-gradient-to-r from-[#7dd3fc] via-[#38bdf8] to-[#60a5fa] bg-clip-text text-transparent">
        {highlight}
      </span>
      {after}
    </>
  );
};

const SectionHeading: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
}> = ({ eyebrow, title, description }) => (
  <motion.div {...reveal} className="mx-auto mb-16 max-w-3xl text-center">
    <span className="eyebrow-pill">{eyebrow}</span>
    <h2 className="mt-5 text-[30px] font-bold tracking-tight text-slate-950 sm:text-[38px]">{title}</h2>
    <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">{description}</p>
  </motion.div>
);

const HeroMetrics: React.FC<{ items: StatItem[] }> = ({ items }) => (
  <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3">
    {items.map((item) => (
      <div
        key={item.label}
        className="rounded-[24px] border border-white/[0.12] bg-white/[0.08] px-4 py-4 backdrop-blur-xl"
      >
        <p className="font-display text-2xl font-bold text-white sm:text-3xl">{item.value}</p>
        <p className="mt-2 text-sm text-slate-300">{item.label}</p>
      </div>
    ))}
  </div>
);

const SolutionHero: React.FC<{
  eyebrow: string;
  title: string;
  highlight?: string;
  description: string;
  heroIcon: IconType;
  heroAccentIcon: IconType;
  solutions: ListCardItem[];
  painPoints: SimpleCardItem[];
}> = ({ eyebrow, title, highlight, description, heroIcon: HeroIcon, heroAccentIcon: AccentIcon, solutions, painPoints }) => (
  <section className="relative overflow-hidden bg-slate-950 pb-24 pt-32 text-white sm:pt-36">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,92,242,0.26),transparent_36%),radial-gradient(circle_at_75%_15%,rgba(14,158,213,0.22),transparent_30%),linear-gradient(135deg,rgba(2,6,23,0.96),rgba(15,23,42,0.9)_42%,rgba(2,6,23,0.98))]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      <HeroIcon className="absolute left-8 top-20 h-28 w-28 text-white/10 sm:left-16 sm:h-36 sm:w-36" />
      <AccentIcon className="absolute bottom-12 right-8 h-24 w-24 text-white/10 sm:right-16 sm:h-32 sm:w-32" />
    </div>

    <div className="relative container-custom grid items-center gap-12 lg:grid-cols-[1.04fr_0.96fr]">
      <motion.div {...reveal} className="text-left">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.08] px-5 py-2.5 text-sm font-medium text-white/90 backdrop-blur-xl">
          <span className="h-2 w-2 rounded-full bg-[#0E9ED5]" />
          {eyebrow}
        </span>
        <h1 className="mt-7 max-w-3xl font-display text-5xl font-bold leading-[1.02] tracking-[-0.04em] text-white sm:text-6xl">
          {renderHighlightedTitle(title, highlight)}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">{description}</p>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row">
          <Link
            to="/contact-us"
            className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#0E9ED5] to-[#385CF2] px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
          >
            Talk to experts
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.08] px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-xl transition-colors hover:bg-white/[0.12] hover:text-white"
          >
            Explore services
          </Link>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {solutions.slice(0, 3).map((item) => (
            <span
              key={item.title}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-sm text-slate-200 backdrop-blur-xl"
            >
              <CheckCircleIcon className="h-4 w-4 text-cyan-300" />
              {item.title}
            </span>
          ))}
        </div>

        <HeroMetrics items={sharedMetrics} />
      </motion.div>

      <motion.div {...reveal} transition={{ duration: 0.8, delay: 0.1 }} className="relative">
        <div className="overflow-hidden rounded-[32px] border border-white/[0.12] bg-white/[0.08] p-5 shadow-[0_40px_120px_-54px_rgba(14,158,213,0.55)] backdrop-blur-2xl sm:p-6">
          <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Execution model</p>
                <h2 className="mt-2 text-2xl font-semibold text-white">Where TrueLog removes friction</h2>
              </div>
              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-200">
                Multi-market ready
              </span>
            </div>

            <div className="mt-6 space-y-3">
              {solutions.slice(0, 3).map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-2.5">
                      <item.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-300">{item.points[0]}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {painPoints.slice(0, 2).map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                  <div className="inline-flex rounded-2xl bg-rose-400/10 p-2.5 text-rose-200">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-white">{item.title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const IndustryHero: React.FC<{
  eyebrow: string;
  title: string;
  highlight?: string;
  description: string;
  heroIcon: IconType;
  heroAccentIcon: IconType;
  stats: StatItem[];
  solutions: SimpleCardItem[];
}> = ({ eyebrow, title, highlight, description, heroIcon: HeroIcon, heroAccentIcon: AccentIcon, stats, solutions }) => (
  <section className="relative overflow-hidden bg-slate-950 pb-24 pt-32 text-white sm:pt-36">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,92,242,0.26),transparent_36%),radial-gradient(circle_at_75%_15%,rgba(14,158,213,0.22),transparent_30%),linear-gradient(135deg,rgba(2,6,23,0.96),rgba(15,23,42,0.9)_42%,rgba(2,6,23,0.98))]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      <HeroIcon className="absolute left-8 top-20 h-28 w-28 text-white/10 sm:left-16 sm:h-36 sm:w-36" />
      <AccentIcon className="absolute bottom-12 right-8 h-24 w-24 text-white/10 sm:right-16 sm:h-32 sm:w-32" />
    </div>

    <div className="relative container-custom grid items-center gap-12 lg:grid-cols-[1.02fr_0.98fr]">
      <motion.div {...reveal} className="text-left">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.08] px-5 py-2.5 text-sm font-medium text-white/90 backdrop-blur-xl">
          <span className="h-2 w-2 rounded-full bg-[#0E9ED5]" />
          {eyebrow}
        </span>
        <h1 className="mt-7 max-w-3xl font-display text-5xl font-bold leading-[1.02] tracking-[-0.04em] text-white sm:text-6xl">
          {renderHighlightedTitle(title, highlight)}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">{description}</p>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row">
          <Link
            to="/contact-us"
            className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#0E9ED5] to-[#385CF2] px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
          >
            Talk to experts
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Link>
          <Link
            to="/global-coverage"
            className="inline-flex items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.08] px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-xl transition-colors hover:bg-white/[0.12] hover:text-white"
          >
            Explore coverage
          </Link>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {solutions.slice(0, 3).map((item) => (
            <span
              key={item.title}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-sm text-slate-200 backdrop-blur-xl"
            >
              <ShieldCheckIcon className="h-4 w-4 text-cyan-300" />
              {item.title}
            </span>
          ))}
        </div>

        <HeroMetrics items={stats} />
      </motion.div>

      <motion.div {...reveal} transition={{ duration: 0.8, delay: 0.1 }} className="relative">
        <div className="overflow-hidden rounded-[32px] border border-white/[0.12] bg-white/[0.08] p-5 shadow-[0_40px_120px_-54px_rgba(14,158,213,0.55)] backdrop-blur-2xl sm:p-6">
          <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Industry fit</p>
            <h2 className="mt-2 text-2xl font-semibold text-white">What operators need in this environment</h2>

            <div className="mt-6 space-y-3">
              {solutions.slice(0, 3).map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-2.5">
                      <item.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-300">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              {stats.map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                  <p className="font-display text-2xl font-bold text-white">{item.value}</p>
                  <p className="mt-2 text-sm text-slate-300">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const FinalCta: React.FC<{
  title: string;
  description: string;
  pills: string[];
  secondaryHref: string;
  secondaryLabel: string;
}> = ({ title, description, pills, secondaryHref, secondaryLabel }) => (
  <section className="bg-slate-950 px-4 pb-16 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl rounded-[34px] border border-white/[0.12] bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-7 shadow-[0_34px_90px_-48px_rgba(15,23,42,0.9)] sm:p-10">
      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Next step</p>
          <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">{title}</h2>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">{description}</p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center rounded-2xl bg-[#0E9ED5] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0b7aa6]"
            >
              Talk to experts
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to={secondaryHref}
              className="inline-flex items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.08] px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/[0.12]"
            >
              {secondaryLabel}
            </Link>
          </div>
        </div>

        <div className="grid gap-3">
          {pills.slice(0, 3).map((pill) => (
            <div key={pill} className="rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
              {pill}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const SolutionPageTemplate: React.FC<SolutionPageTemplateProps> = ({
  eyebrow,
  title,
  highlight,
  description,
  heroIcon,
  heroAccentIcon,
  problemHeading,
  problemDescription,
  painPoints,
  solutionsIntro,
  solutions,
  whyChooseCards,
  ctaTitle,
  ctaDescription,
}) => (
  <div className="bg-white">
    {warehouseTheme ? <PageHero
      eyebrow={eyebrow}
      heroIcon={heroIcon}
      accentIcon={heroAccentIcon}
      title={title}
      highlight={highlight}
      description={description}
      actions={[{ label: 'Talk to experts', href: '/contact-us' }, { label: 'Explore services', href: '/services', variant: 'secondary' }]}
      badges={solutions.slice(0, 3).map((item) => item.title)}
      metrics={sharedMetrics.map(({ value, label }) => ({ value, label }))}
      panelEyebrow="Execution model"
      panelTitle="Where TrueLog removes friction"
      panelItems={solutions.slice(0, 3).map((item) => ({ icon: item.icon, title: item.title, description: item.points[0] }))}
    /> : <SolutionHero
      eyebrow={eyebrow}
      title={title}
      highlight={highlight}
      description={description}
      heroIcon={heroIcon}
      heroAccentIcon={heroAccentIcon}
      solutions={solutions}
      painPoints={painPoints}
    />}

    <section className="bg-white py-20">
      <div className="container-custom">
        <SectionHeading eyebrow="Key Friction" title={problemHeading} description={problemDescription} />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {painPoints.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.07 }}
              className="rounded-[28px] border border-slate-200/80 bg-gradient-to-b from-rose-50 to-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.36)]"
            >
              <div className="inline-flex rounded-2xl bg-rose-100 p-3 text-rose-600">
                <item.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-slate-50 py-20">
      <div className="container-custom">
        <SectionHeading
          eyebrow="Execution Layer"
          title="How TrueLog Helps"
          description={solutionsIntro}
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {solutions.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.06 }}
              className="rounded-[30px] border border-white bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
            >
              <div className="inline-flex rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                <item.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-950">{item.title}</h3>
              <ul className="mt-5 space-y-3">
                {item.points.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm leading-7 text-slate-600">
                    <CheckCircleIcon className="mt-1 h-4 w-4 flex-shrink-0 text-[#0E9ED5]" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white py-20">
      <div className="container-custom">
        <SectionHeading
          eyebrow="Why TrueLog"
          title="A stronger operating model for global execution"
          description="One accountable partner across compliance, movement, deployment, and returns."
        />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {whyChooseCards.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.07 }}
              className="relative overflow-hidden rounded-[30px] border border-slate-900/20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-7 text-white shadow-[0_28px_70px_-42px_rgba(15,23,42,0.85)]"
            >
              <div className={`absolute inset-x-0 top-0 h-1 ${item.accent ?? 'bg-cyan-500'}`} />
              <div className="inline-flex rounded-2xl bg-white/[0.08] p-3 text-cyan-200">
                <item.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-2xl font-semibold text-white">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-300">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <FinalCta
      title={ctaTitle}
      description={ctaDescription}
      pills={whyChooseCards.map((item) => item.title)}
      secondaryHref="/services"
      secondaryLabel="Explore services"
    />
  </div>
);

export const IndustryPageTemplate: React.FC<IndustryPageTemplateProps> = ({
  eyebrow,
  title,
  highlight,
  description,
  heroIcon,
  heroAccentIcon,
  challengesIntro,
  challenges,
  solutionsIntro,
  solutions,
  useCase,
  stats,
  ctaTitle,
  ctaDescription,
}) => (
  <div className="bg-white">
    {warehouseTheme ? <PageHero
      eyebrow={eyebrow}
      heroIcon={heroIcon}
      accentIcon={heroAccentIcon}
      title={title}
      highlight={highlight}
      description={description}
      actions={[{ label: 'Talk to experts', href: '/contact-us' }, { label: 'Explore coverage', href: '/global-coverage', variant: 'secondary' }]}
      badges={solutions.slice(0, 3).map((item) => item.title)}
      metrics={stats.map(({ value, label }) => ({ value, label }))}
      panelEyebrow="Built around your operations"
      panelTitle="The right logistics support"
      panelItems={solutions.slice(0, 3).map((item) => ({ icon: item.icon, title: item.title, description: item.description }))}
    /> : <IndustryHero
      eyebrow={eyebrow}
      title={title}
      highlight={highlight}
      description={description}
      heroIcon={heroIcon}
      heroAccentIcon={heroAccentIcon}
      stats={stats}
      solutions={solutions}
    />}

    <section className="bg-white py-20">
      <div className="container-custom">
        <SectionHeading eyebrow="Key Challenges" title="Operational friction to solve first" description={challengesIntro} />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {challenges.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.07 }}
              className="rounded-[28px] border border-slate-200/80 bg-gradient-to-b from-rose-50 to-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.36)]"
            >
              <div className="inline-flex rounded-2xl bg-rose-100 p-3 text-rose-600">
                <item.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-slate-50 py-20">
      <div className="container-custom">
        <SectionHeading
          eyebrow="How TrueLog Helps"
          title="Execution built for this environment"
          description={solutionsIntro}
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {solutions.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.06 }}
              className="rounded-[30px] border border-white bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
            >
              <div className="inline-flex rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                <item.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white py-20">
      <div className="container-custom grid items-start gap-8 lg:grid-cols-[1.08fr_0.92fr]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.75 }}
          className="rounded-[32px] border border-slate-200/80 bg-gradient-to-br from-[#eff6ff] via-white to-[#ecfeff] p-8 shadow-[0_28px_70px_-44px_rgba(15,23,42,0.4)]"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2447d8]">Operating scenario</p>
          <h2 className="mt-4 text-3xl font-bold text-slate-950">Typical use case</h2>
          <p className="mt-4 text-base leading-8 text-slate-700">{useCase}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.75, delay: 0.08 }}
          className="rounded-[32px] border border-slate-900/18 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8 text-white shadow-[0_28px_70px_-44px_rgba(15,23,42,0.8)]"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Delivery proof</p>
          <div className="mt-5 grid grid-cols-2 gap-4">
            {stats.map((item) => (
              <div key={item.label} className="rounded-[24px] border border-white/10 bg-white/[0.06] p-4">
                <p className="font-display text-3xl font-bold text-white">{item.value}</p>
                <p className="mt-2 text-sm text-slate-300">{item.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>

    <FinalCta
      title={ctaTitle}
      description={ctaDescription}
      pills={solutions.map((item) => item.title)}
      secondaryHref="/global-coverage"
      secondaryLabel="Explore coverage"
    />
  </div>
);
